import { useEffect, useRef } from 'react'

// Confirm dialog for the destructive-ish admin actions (M1.5). Small enough to
// own outright: focus is trapped while open and restored on close, Escape and
// an overlay click both cancel, and the page behind stops scrolling. `danger`
// switches to a red-tinted shell for an unrecoverable action; nothing passes it
// today — the transcript purge that needed it is no longer HR-facing (M4.3).
// `wide` is the form-sized shell (the New space dialog): same trap, same scrim,
// a 640px body that goes full-screen on a phone.
export default function ConfirmDialog({
  label,
  onClose,
  danger = false,
  wide = false,
  className = '',
  children,
}) {
  const dialogRef = useRef(null)

  useEffect(() => {
    const previouslyFocused = document.activeElement
    const dialog = dialogRef.current
    const focusables = () =>
      [...dialog.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )].filter((el) => !el.disabled)
    focusables()[0]?.focus()

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    function handleKey(event) {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
        return
      }
      if (event.key !== 'Tab') return
      const items = focusables()
      if (items.length === 0) return
      const first = items[0]
      const last = items[items.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    dialog.addEventListener('keydown', handleKey)
    return () => {
      dialog.removeEventListener('keydown', handleKey)
      document.body.style.overflow = previousOverflow
      previouslyFocused?.focus?.()
    }
  }, [onClose])

  return (
    <div
      className="modal-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={dialogRef}
        className={`modal${danger ? ' modal--danger' : ''}${wide ? ' modal--wide' : ''}${
          className ? ` ${className}` : ''
        }`}
        role="dialog"
        aria-modal="true"
        aria-label={label}
      >
        {children}
      </div>
    </div>
  )
}
